/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Button, Skeleton, Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';
import type { Skill } from '@coursera/grpc-types-skills/coursera/proto/skills/v2/skill';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { useBrowseInSerpRelatedSkills } from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSerpUtils';
import { ON_SITE_SEARCH_PATH, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchResult } from 'bundles/search-common/providers/searchTypes';
import { isInFilterTestVariant } from 'bundles/search-common/utils/experimentUtils';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';

import _t from 'i18n!nls/search-common';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type ButtonProp = {
  buttonText: string;
  searchResults: SearchResult | undefined;
  skillCardIndex: number;
  skillId: string;
};

const styles = {
  relatedSkillsContainer: css`
    margin: auto;
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-400);
    max-width: ${breakpoints.values.lg}px;

    ul {
      list-style-type: none;
    }
  `,
  relatedSkillsBody: css`
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: var(--cds-spacing-150);

    /* a11y when tabbing need padding */
    padding: var(--cds-spacing-100) var(--cds-spacing-50);
    margin: var(--cds-spacing-100) -4px 0 -4px;
  `,
  relatedSkillsButton: css`
    padding: var(--cds-spacing-200);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 10px;
    color: var(--cds-color-neutral-primary);
    background-color: var(--cds-color-neutral-background-primary);
    min-width: 181px;
    min-height: 80px;
    width: 181px;
    height: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    text-align: center;
    box-shadow: none;
    text-decoration: none;

    &:hover {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-interactive-background-primary-hover-weak);
      text-decoration: underline var(--cds-color-interactive-primary-hover);
    }
  `,
  relatedSkillsButtonLoading: css`
    padding: 0 var(--cds-spacing-200);
  `,
};

const MAX_RELATED_SKILLS = 7;
const MIN_RELATED_SKILLS = 2;

export const hasMatchingFilterSkill = (searchData: Pick<SearchResult, 'facets'>, skillName: string) => {
  if (!searchData) {
    return false;
  }

  const skillFacet = searchData.facets?.find((facet) => facet.name === 'skills');
  const matchingSkill = skillFacet?.valuesAndCounts.find(
    (facetValue) => facetValue.value.toLowerCase() === skillName.toLowerCase()
  )?.value;

  return !!matchingSkill;
};

export const getNavigationLink = (
  searchResult: Pick<SearchResult, 'facets'> | undefined,
  relatedSkillName: string
): string => {
  const hasMatchingSkill = searchResult && hasMatchingFilterSkill(searchResult, relatedSkillName);

  if (hasMatchingSkill) {
    const searchUrl = `${ON_SITE_SEARCH_PATH}?query=${relatedSkillName}&skills=${relatedSkillName}`;
    return searchUrl;
  } else {
    const searchUrl = `${ON_SITE_SEARCH_PATH}?query=${relatedSkillName}`;
    return searchUrl;
  }
};

const SerpRelatedSkillButton = ({ buttonText, searchResults, skillCardIndex, skillId }: ButtonProp) => {
  const track = useTracker();
  const trackingData: EventData<'click_topic_card'> = {
    topicId: skillId,
    topicCardIndex: skillCardIndex,
  };
  const navigate = useNavigate();
  const {
    query: { language },
  } = useLocation();
  const hasEnglishLanguageInQuery = typeof language === 'string' && language === 'English';
  const hasLanguageFilter = isInFilterTestVariant() && hasEnglishLanguageInQuery;
  const appendUrl = hasLanguageFilter ? '&language=English' : '';

  const handleClick = (skillName: string) => {
    track('click_topic_card', trackingData);
    const searchUrl = `${getNavigationLink(searchResults, skillName)}${appendUrl}`;
    navigate(searchUrl);
  };
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_topic_card',
    trackingData,
    [],
    { fullyVisible: true }
  );

  return (
    <li>
      <TrackedButton
        _refAltAsTrueRef={visibilityTrackingRef}
        trackingName="related_skill"
        trackingData={{ skillName: buttonText, skillId }}
        onClick={() => {
          handleClick(buttonText);
        }}
        data-testid="related-skill"
        css={styles.relatedSkillsButton}
        variant="ghost"
      >
        <Typography2 component="p" variant="actionPrimary">
          {buttonText}
        </Typography2>
      </TrackedButton>
    </li>
  );
};

const EqpRelatedSkillButton = ({ buttonText, searchResults, skillCardIndex, skillId }: ButtonProp) => {
  const track = useTracker();

  const trackingData: EventData<'click_topic_card'> = {
    topicId: skillId,
    topicCardIndex: skillCardIndex,
  };
  const redirectLink = getNavigationLink(searchResults, buttonText);
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_topic_card',
    trackingData,
    [],
    { fullyVisible: true }
  );

  return (
    <li>
      <TrackedLink2
        linkType="a"
        onClick={() => {
          track('click_topic_card', trackingData);
        }}
        data={{ skillName: buttonText, skillId }}
        trackingName="related_skill"
        href={redirectLink}
        data-testid="related-skill"
        css={styles.relatedSkillsButton}
      >
        <Typography2 ref={visibilityTrackingRef} component="p" variant="actionPrimary">
          {buttonText}
        </Typography2>
      </TrackedLink2>
    </li>
  );
};

export const RelatedSkillsLoading = () => (
  <div css={styles.relatedSkillsContainer}>
    <Skeleton height={24} variant="text" width="75%" />
    <div css={styles.relatedSkillsBody}>
      {Array.from({ length: MAX_RELATED_SKILLS }).map((_, index) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={`related-skills-loading-${index}`}
          data-testid="related-skills-skeleton"
          css={[styles.relatedSkillsButton, styles.relatedSkillsButtonLoading]}
        >
          <div style={{ width: '100%' }}>
            <Skeleton height={24} variant="text" width="100%" />
            <Skeleton height={24} variant="text" width="50%" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RelatedSkills = ({ skill: currentSkill }: { skill: Skill }) => {
  const isEqpRoute = useIsEqp();
  const queryResponse = useBrowseInSerpRelatedSkills(currentSkill);
  const relatedSkills = queryResponse?.data?.slice(0, MAX_RELATED_SKILLS);

  const { getSearchResults } = useSearch();
  const searchResults = useMemo(() => getSearchResults(SEARCH_MAIN_INDEX_ID), [getSearchResults]);

  if (queryResponse?.loading && !relatedSkills) {
    return <RelatedSkillsLoading />;
  }

  if (!queryResponse?.loading && (!relatedSkills || relatedSkills.length < MIN_RELATED_SKILLS)) {
    return null;
  }

  const eqpRelatedSkills = relatedSkills?.map((skill, index) => (
    <EqpRelatedSkillButton
      buttonText={skill.skillName}
      skillCardIndex={index}
      key={skill.skillId}
      skillId={skill.skillId}
      searchResults={searchResults}
    />
  ));
  const serpRelatedSkills = relatedSkills?.map((skill, index) => (
    <SerpRelatedSkillButton
      buttonText={skill.skillName}
      skillCardIndex={index}
      key={skill.skillId}
      skillId={skill.skillId}
      searchResults={searchResults}
    />
  ));

  return (
    <section css={styles.relatedSkillsContainer}>
      <Typography2 component="h2" variant="subtitleLarge">
        {_t('Explore #{currentSkill} skills', {
          currentSkill: currentSkill.skillName,
        })}
      </Typography2>
      <ul css={styles.relatedSkillsBody}>{isEqpRoute ? eqpRelatedSkills : serpRelatedSkills}</ul>
    </section>
  );
};

export default RelatedSkills;
